import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/adminApi';
import { useAdmin } from '../../contexts/AdminContext';
import EventForm from './EventForm';
import './EventManagement.css';

const EventManagement = () => {
  const { admin } = useAdmin();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    search: '',
    city: '',
    status: '',
    date_from: '',
    date_to: ''
  });
  const [pagination, setPagination] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [cities, setCities] = useState([]);
  const [formLoading, setFormLoading] = useState(false);

  // Load events
  const loadEvents = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getEvents(filters);
      setEvents(response.events);
      setPagination(response.pagination);
      setError(null);
    } catch (err) {
      setError('Events yüklenirken hata oluştu');
      console.error('Load events error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load cities
  const loadCities = async () => {
    try {
      const citiesData = await adminApi.getCities();
      setCities(citiesData);
    } catch (err) {
      console.error('Load cities error:', err);
    }
  };

  useEffect(() => {
    loadEvents();
  }, [filters]);

  useEffect(() => {
    loadCities();
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Bu eventi silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await adminApi.deleteEvent(eventId);
      loadEvents(); // Refresh list
      alert('Event başarıyla silindi');
    } catch (err) {
      alert('Event silinirken hata oluştu');
      console.error('Delete event error:', err);
    }
  };

  const handleCreateEvent = async (eventData) => {
    setFormLoading(true);
    try {
      await adminApi.createEvent(eventData);
      setShowCreateModal(false);
      loadEvents(); // Refresh list
      alert('Event başarıyla eklendi');
    } catch (err) {
      console.error('Create event error:', err);
      throw err; // Re-throw to let EventForm handle the error
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateEvent = async (eventData) => {
    setFormLoading(true);
    try {
      await adminApi.updateEvent(editingEvent.id, eventData);
      setEditingEvent(null);
      loadEvents(); // Refresh list
      alert('Event başarıyla güncellendi');
    } catch (err) {
      console.error('Update event error:', err);
      throw err; // Re-throw to let EventForm handle the error
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloseCreateModal = () => {
    if (!formLoading) {
      setShowCreateModal(false);
    }
  };

  const handleCloseEditModal = () => {
    if (!formLoading) {
      setEditingEvent(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEventTypeIcon = (type) => {
    switch (type) {
      case 'concert': return '🎵';
      case 'sports': return '⚽';
      case 'conference': return '🎤';
      case 'festival': return '🎪';
      case 'other': return '📅';
      default: return '📅';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return '✅';
      case 'completed': return '✔️';
      case 'cancelled': return '❌';
      case 'draft': return '📝';
      default: return '❓';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#dc2626';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const isEventUpcoming = (eventDate) => {
    return new Date(eventDate) > new Date();
  };

  return (
    <div className="event-management">
      <div className="management-header">
        <div className="header-content">
          <h1>📅 Event Yönetimi</h1>
          <p>Tüm eventleri görüntüleyin, düzenleyin ve yönetin</p>
        </div>
        <button 
          className="create-btn"
          onClick={() => setShowCreateModal(true)}
        >
          <span className="btn-icon">➕</span>
          Yeni Event Ekle
        </button>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>🔍 Arama</label>
            <input
              type="text"
              placeholder="Event adı, açıklama..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <label>🌍 Şehir</label>
            <select
              value={filters.city}
              onChange={(e) => handleFilterChange('city', e.target.value)}
            >
              <option value="">Tüm şehirler</option>
              {cities.map(city => (
                <option key={city.city} value={city.city}>
                  {city.city} ({city.count})
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>📊 Durum</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">Tüm durumlar</option>
              <option value="active">Aktif</option>
              <option value="completed">Tamamlandı</option>
              <option value="cancelled">İptal Edildi</option>
              <option value="draft">Taslak</option>
            </select>
          </div>

          <div className="filter-group">
            <label>📅 Başlangıç Tarihi</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>📅 Bitiş Tarihi</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>📄 Sayfa başına</label>
            <select
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      {pagination && (
        <div className="results-summary">
          <span className="summary-text">
            {pagination.total_items} eventden {((pagination.current_page - 1) * pagination.items_per_page) + 1}-
            {Math.min(pagination.current_page * pagination.items_per_page, pagination.total_items)} arası gösteriliyor
          </span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
          <button onClick={loadEvents} className="retry-btn">Tekrar Dene</button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Event listesi yükleniyor...</p>
        </div>
      ) : (
        <>
          {/* Events Table */}
          <div className="events-table-container">
            <table className="events-table">
              <thead>
                <tr>
                  <th>📅 Event</th>
                  <th>📍 Lokasyon</th>
                  <th>⏰ Tarih & Saat</th>
                  <th>📊 Durum</th>
                  <th>🎯 Öncelik</th>
                  <th>👥 Katılımcı</th>
                  <th>⚙️ İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {events.map(event => (
                  <tr key={event.id} className={`event-row ${isEventUpcoming(event.event_date) ? 'upcoming' : 'past'}`}>
                    <td className="event-info">
                      <div className="event-icon">
                        {getEventTypeIcon(event.event_type)}
                      </div>
                      <div className="event-details">
                        <div className="event-name">
                          {event.name}
                        </div>
                        <div className="event-type">
                          {event.event_type} • ID: {event.id}
                        </div>
                        {event.description && (
                          <div className="event-description">
                            {event.description.length > 50 
                              ? event.description.substring(0, 50) + '...'
                              : event.description
                            }
                          </div>
                        )}
                      </div>
                    </td>
                    
                    <td className="location-info">
                      <div className="city-tag">{event.city}</div>
                      {event.location && (
                        <div className="location-detail">
                          📍 {event.location}
                        </div>
                      )}
                      {event.latitude && event.longitude && (
                        <div className="coordinates">
                          📐 {parseFloat(event.latitude).toFixed(4)}, {parseFloat(event.longitude).toFixed(4)}
                        </div>
                      )}
                    </td>
                    
                    <td className="date-info">
                      <div className="event-date">
                        {formatDate(event.event_date)}
                      </div>
                      {event.end_date && (
                        <div className="end-date">
                          Bitiş: {formatDate(event.end_date)}
                        </div>
                      )}
                      <div className={`time-indicator ${isEventUpcoming(event.event_date) ? 'upcoming' : 'past'}`}>
                        {isEventUpcoming(event.event_date) ? '🔜 Yaklaşan' : '⏳ Geçmiş'}
                      </div>
                    </td>
                    
                    <td className="status-info">
                      <span className={`status-badge status-${event.status}`}>
                        {getStatusIcon(event.status)} {event.status}
                      </span>
                    </td>
                    
                    <td className="priority-info">
                      <div 
                        className="priority-indicator"
                        style={{ color: getPriorityColor(event.priority) }}
                      >
                        <span className="priority-dot" style={{ backgroundColor: getPriorityColor(event.priority) }}></span>
                        {event.priority || 'normal'}
                      </div>
                    </td>
                    
                    <td className="participants-info">
                      <div className="participant-count">
                        👥 {event.max_participants || 'Sınırsız'}
                      </div>
                      {event.current_participants !== undefined && (
                        <div className="current-participants">
                          Mevcut: {event.current_participants}
                        </div>
                      )}
                    </td>
                    
                    <td className="actions">
                      <div className="action-buttons">
                        <button
                          className="action-btn edit"
                          onClick={() => setEditingEvent(event)}
                          title="Düzenle"
                        >
                          ✏️
                        </button>
                        {admin?.role === 'SUPER_ADMIN' && (
                          <button
                            className="action-btn delete"
                            onClick={() => handleDeleteEvent(event.id)}
                            title="Sil"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {events.length === 0 && !loading && (
            <div className="empty-state">
              <div className="empty-icon">📅</div>
              <h3>Event bulunamadı</h3>
              <p>Arama kriterlerinize uygun event bulunmuyor.</p>
              <button 
                className="clear-filters-btn"
                onClick={() => setFilters({
                  page: 1,
                  limit: 10,
                  search: '',
                  city: '',
                  status: '',
                  date_from: '',
                  date_to: ''
                })}
              >
                Filtreleri Temizle
              </button>
            </div>
          )}

          {/* Pagination */}
          {pagination && pagination.total_pages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
              >
                ⬅️ Önceki
              </button>
              
              <div className="pagination-info">
                {Array.from({ length: pagination.total_pages }, (_, i) => i + 1)
                  .filter(page => 
                    page === 1 || 
                    page === pagination.total_pages || 
                    Math.abs(page - pagination.current_page) <= 1
                  )
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] < page - 1 && (
                        <span className="pagination-ellipsis">...</span>
                      )}
                      <button
                        className={`pagination-number ${page === pagination.current_page ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  ))
                }
              </div>
              
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.total_pages}
              >
                Sonraki ➡️
              </button>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) handleCloseCreateModal();
        }}>
          <div className="modal-content event-form-modal">
            <EventForm
              event={null}
              cities={cities}
              onSubmit={handleCreateEvent}
              onCancel={handleCloseCreateModal}
              isLoading={formLoading}
            />
          </div>
        </div>
      )}

      {editingEvent && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) handleCloseEditModal();
        }}>
          <div className="modal-content event-form-modal">
            <EventForm
              event={editingEvent}
              cities={cities}
              onSubmit={handleUpdateEvent}
              onCancel={handleCloseEditModal}
              isLoading={formLoading}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EventManagement;
